import React, { useState, useEffect } from 'react';
import { Trash2, Mail, Phone, User, MessageSquare, CheckCircle, Clock, Eye } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    const token = localStorage.getItem('adminToken');
    try {
      await axios.put(`${API_BASE_URL}/api/admin/contacts/${id}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchContacts();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const deleteContact = async (id) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      const token = localStorage.getItem('adminToken');
      try {
        await axios.delete(`${API_BASE_URL}/api/admin/contacts/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchContacts();
      } catch (error) {
        console.error('Error deleting contact:', error);
      }
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      contacted: 'bg-blue-500/20 text-blue-400',
      completed: 'bg-green-500/20 text-green-400'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400';
  };

  const getStatusIcon = (status) => {
    if (status === 'pending') return <Clock size={14} className="text-yellow-400" />;
    if (status === 'contacted') return <Eye size={14} className="text-blue-400" />;
    return <CheckCircle size={14} className="text-green-400" />;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-10 h-10 border-3 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">Contact Messages</h1>

      <div className="space-y-4">
        {contacts.length === 0 ? (
          <div className="bg-dark-900 rounded-lg p-8 text-center text-gray-500 border border-dark-700">
            No contact messages found
          </div>
        ) : (
          contacts.map((contact) => (
            <div key={contact.id} className="bg-dark-900 rounded-lg border border-dark-700 hover:border-orange-500/30 transition-all duration-200">
              <div className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-4 mb-3">
                      <div className="flex items-center space-x-2">
                        <div className="bg-orange-500/20 p-1.5 rounded-full">
                          <User size={14} className="text-orange-500" />
                        </div>
                        <span className="font-medium text-white">{contact.name}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="bg-blue-500/20 p-1.5 rounded-full">
                          <Mail size={14} className="text-blue-400" />
                        </div>
                        <a href={`mailto:${contact.email}`} className="text-blue-400 hover:underline text-sm">
                          {contact.email}
                        </a>
                      </div>
                      {contact.phone && (
                        <div className="flex items-center space-x-2">
                          <div className="bg-green-500/20 p-1.5 rounded-full">
                            <Phone size={14} className="text-green-400" />
                          </div>
                          <a href={`tel:${contact.phone}`} className="text-gray-400 text-sm hover:text-white">
                            {contact.phone}
                          </a>
                        </div>
                      )}
                    </div>

                    {contact.subject && (
                      <div className="mb-2">
                        <span className="text-gray-400 text-sm">Subject:</span>
                        <span className="text-white text-sm ml-2">{contact.subject}</span>
                      </div>
                    )}

                    <div className="bg-dark-800 rounded-lg p-3 mt-2 border border-dark-700">
                      <div className="flex items-center space-x-2 mb-2">
                        <MessageSquare size={14} className="text-orange-500" />
                        <span className="text-sm font-medium text-gray-300">Message:</span>
                      </div>
                      <p className="text-gray-400 text-sm leading-relaxed">{contact.message}</p>
                    </div>

                    <div className="text-xs text-gray-500 mt-3">
                      Received: {new Date(contact.created_at).toLocaleString()}
                    </div>
                  </div>

                  <div className="flex flex-col items-end space-y-3 ml-4">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(contact.status)}
                      <select
                        value={contact.status}
                        onChange={(e) => updateStatus(contact.id, e.target.value)}
                        className={`text-xs px-2 py-1 rounded-full border border-dark-600 bg-dark-800 ${getStatusBadge(contact.status)} focus:outline-none focus:border-orange-500 cursor-pointer`}
                      >
                        <option value="pending">Pending</option>
                        <option value="contacted">Contacted</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                    <button
                      onClick={() => deleteContact(contact.id)}
                      className="text-red-400 hover:text-red-300 transition p-1"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminContacts;